import React, { useState, useEffect } from 'react';
import { updateCheckService, UpdateCheckResult } from '../../services/updateCheckService';
import './UpdateBanner.css';

interface UpdateBannerProps {
  checkInterval?: number; // Intervalo en milisegundos
  showDismiss?: boolean; // Si se puede cerrar el banner
}

const UpdateBanner: React.FC<UpdateBannerProps> = ({ 
  checkInterval = 5 * 60 * 1000, // 5 minutos por defecto
  showDismiss = true 
}) => {
  const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar actualizaciones al montar el componente
  useEffect(() => {
    checkForUpdates();
    
    // Configurar verificación periódica
    const interval = setInterval(checkForUpdates, checkInterval);
    
    return () => clearInterval(interval);
  }, [checkInterval]);

  const checkForUpdates = async () => {
    try {
      setIsLoading(true);
      const result = await updateCheckService.checkForUpdates();
      setUpdateInfo(result);
      
      if (result.hasUpdate) {
        setIsVisible(true);
        console.log('🚀 Nueva versión disponible:', {
          actual: result.currentHash,
          nueva: result.latestHash
        });
      }
    } catch (error) {
      console.error('Error al verificar actualizaciones:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReload = () => {
    console.log('🔄 Iniciando recarga de la aplicación...');
    
    // Deshabilitar el botón inmediatamente para evitar múltiples clics
    setIsLoading(true);
    
    // Limpiar sessionStorage de forma síncrona
    try {
      sessionStorage.clear();
      console.log('✅ SessionStorage limpiado');
    } catch (error) {
      console.warn('⚠️ Error al limpiar sessionStorage:', error);
    }
    
    // Recargar inmediatamente sin operaciones asíncronas
    console.log('🔄 Recargando página...');
    window.location.reload();
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleForceCheck = async () => {
    await checkForUpdates();
  };

  // No mostrar nada si no hay actualización o si está cargando
  if (!isVisible || !updateInfo?.hasUpdate) {
    return null;
  }

  return (
    <div className="update-banner">
      <div className="update-banner-content">
        <div className="update-banner-icon">
          🔄
        </div>
        
        <div className="update-banner-message">
          <strong>Nueva versión disponible</strong>
          <span>Recarga la aplicación para obtener las últimas mejoras.</span>
          {updateInfo && (
            <small>
              Versión actual: {updateInfo.currentVersion}-{updateInfo.currentHash} | 
              Nueva versión: {updateInfo.latestVersion}-{updateInfo.latestHash}
            </small>
          )}
        </div>

        <div className="update-banner-actions">
          <button 
            className="update-banner-reload-btn"
            onClick={handleReload}
            title="Recargar aplicación"
            disabled={isLoading}
          >
            {isLoading ? '⏳ Recargando...' : '🔄 Recargar'}
          </button>
          
          <button 
            className="update-banner-check-btn"
            onClick={handleForceCheck}
            title="Verificar manualmente"
            disabled={isLoading}
          >
            {isLoading ? '⏳' : '🔄'}
          </button>

          {showDismiss && (
            <button 
              className="update-banner-dismiss-btn"
              onClick={handleDismiss}
              title="Cerrar banner"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
